// ** React Imports
import React, { ReactNode } from 'react'

// ** MUI Imports
import { Theme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

// ** Layout Imports
// !Do not remove this Layout import
import Layout from 'src/@core/layouts/Layout'

// ** Navigation Imports
import VerticalNavItems, {
  FranchiseSubNavigation,
  ProductSubNavigation,
  SubNavigation,
  SubNavigationMobile,
  SubNavigationRestaurant
} from 'src/gyg-navigation/vertical'
import HorizontalNavItems from 'src/gyg-navigation/horizontal'

// ** Component Import
// Uncomment the below line (according to the layout type) when using server-side menu
// import ServerSideVerticalNavItems from './components/vertical/ServerSideNavItems'
// import ServerSideHorizontalNavItems from './components/horizontal/ServerSideNavItems'

import VerticalAppBarContent from './components/vertical/AppBarContent'
import HorizontalAppBarContent from './components/horizontal/AppBarContent'
import GlobalAnnouncementBanner from '@/src/components/GlobalAnnouncementBanner'

// ** Hook Import
import { useSettings } from 'src/@core/hooks/useSettings'
import { useAuth } from '../hooks/useAuth'
import { useGrowthBook } from '@/src/context/GrowthBookProvider'

import { setGrowthBookAttributes } from '../lib/growthbook'

// ** Iframe Utils
import { isIframeContext } from '@/src/utils/iframeUtils'

interface Props {
  children: ReactNode
  contentHeightFixed?: boolean
}

const UserLayout = ({ children, contentHeightFixed }: Props) => {
  // ** Hooks
  const { settings, saveSettings } = useSettings()

  // ** Vars for server side navigation
  // const { menuItems: verticalMenuItems } = ServerSideVerticalNavItems()
  // const { menuItems: horizontalMenuItems } = ServerSideHorizontalNavItems()

  /**
   *  The below variable will hide the current layout menu at given screen size.
   *  The menu will be accessible from the Hamburger icon only (Vertical Overlay Menu).
   *  You can change the screen size from which you want to hide the current layout menu.
   *  Please refer useMediaQuery() hook: https://mui.com/material-ui/react-use-media-query/,
   *  to know more about what values can be passed to this hook.
   *  ! Do not change this value unless you know what you are doing. It can break the template.
   */
  const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))

  if (hidden && settings.layout === 'horizontal') {
    settings.layout = 'vertical'
  }

  // Check if this is an iframe context
  const isIframe = isIframeContext()

  // get logged in user data
  const { user } = useAuth()

  // get GrowthBook instance
  const growthbook = useGrowthBook()

  // set user attributes for GrowthBook in useEffect to avoid render-time state updates
  React.useEffect(() => {
    if (user) {
      setGrowthBookAttributes({
        id: user?.id,
        email: user?.email,
        role: user?.role?.name,
        userAgent: navigator.userAgent,
        scope: user?.role?.scope
      })
    }
  }, [user])

  // Call navigation functions at component level to ensure hooks are called consistently
  const navItems = VerticalNavItems(growthbook, { user })
  const subNavItems = SubNavigation()
  const subNavItemsMobile = SubNavigationMobile()
  const subNavigationRestaurant = SubNavigationRestaurant()
  const productSubNavItems = ProductSubNavigation()
  const franchiseSubNavItems = FranchiseSubNavigation()

  return (
    <Layout
      hidden={hidden}
      settings={{
        ...settings,

        // Hide navigation in iframe context
        navHidden: isIframe ? true : settings.navHidden
      }}
      saveSettings={saveSettings}
      contentHeightFixed={contentHeightFixed}
      verticalLayoutProps={{
        navMenu: {
          navItems,
          subNavItems,
          subNavItemsMobile,
          subNavigationRestaurant,
          productSubNavItems,
          franchiseSubNavItems
        },
        appBar: {
          content: props => (
            <VerticalAppBarContent
              hidden={hidden}
              settings={settings}
              saveSettings={saveSettings}
              toggleNavVisibility={props.toggleNavVisibility}
            />
          )
        }
      }}
      horizontalLayoutProps={{
        navMenu: {
          navItems,
          subNavItems,
          subNavItemsMobile,
          subNavigationRestaurant,
          productSubNavItems,
          franchiseSubNavItems
        },
        appBar: {
          content: props => (
            <VerticalAppBarContent
              hidden={hidden}
              settings={settings}
              saveSettings={saveSettings}
              toggleNavVisibility={props.toggleNavVisibility}
            />
          )
        }
      }}
      {...(settings.layout === 'horizontal' && {
        horizontalLayoutProps: {
          navMenu: {
            navItems: HorizontalNavItems(),
            subNavItems,
            subNavItemsMobile,
            subNavigationRestaurant,
            productSubNavItems,
            franchiseSubNavItems

            // Uncomment the below line when using server-side menu in horizontal layout and comment the above line
            // navItems: horizontalMenuItems
          },
          appBar: {
            content: () => <HorizontalAppBarContent hidden={hidden} settings={settings} saveSettings={saveSettings} />
          }
        }
      })}
    >
      <GlobalAnnouncementBanner>{children}</GlobalAnnouncementBanner>
    </Layout>
  )
}

export default UserLayout
